import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { BarChart, Bar, PieChart, Pie, Cell, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Treemap, FunnelChart, Funnel, LabelList } from 'recharts';
import { ResponsiveGridLayout } from 'react-grid-layout';
import { FolderKanban, TrendingUp, UserCheck, Briefcase, DollarSign, BarChart3, PieChart as PieIcon, ArrowUpRight, Settings, X, Plus, GripVertical, Eye, EyeOff, LayoutDashboard, Save } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { dashboardAPI } from '../services/api';
import { WorldMapWidget, StateHeatmapWidget, CityHeatmapWidget } from '../components/dashboard/MapWidgets';
import { useCurrencyToggle } from '../hooks/useCurrencyToggle';
import 'react-grid-layout/css/styles.css';

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316', '#6366f1', '#84cc16'];
const URGENCY_COLORS_MAP = { 'Very High': '#ef4444', 'High': '#f97316', 'Medium': '#eab308', 'Low': '#22c55e', 'No': '#94a3b8' };

const fmt = (val) => {
  if (val === null || val === undefined) return '-';
  if (val >= 10000000) return `${(val / 10000000).toFixed(2)} Cr`;
  if (val >= 100000) return `${(val / 100000).toFixed(2)} L`;
  if (val >= 1000) return `${(val / 1000).toFixed(1)} K`;
  return val.toLocaleString('en-IN');
};

const CHART_WIDGETS = [
  { id: 'fund_aum', title: 'Fund-wise AUM', type: 'bar', defaultSize: { w: 6, h: 4 } },
  { id: 'investor_type', title: 'Investor Type Distribution', type: 'pie', defaultSize: { w: 3, h: 4 } },
  { id: 'coverage_urgency', title: 'Coverage Urgency', type: 'pie', defaultSize: { w: 3, h: 4 } },
  { id: 'city_dist', title: 'Investors by City', type: 'bar', defaultSize: { w: 6, h: 4 } },
  { id: 'state_dist', title: 'Investors by State', type: 'bar', defaultSize: { w: 6, h: 4 } },
  { id: 'contribution_timeline', title: 'Contribution Over Time', type: 'line', defaultSize: { w: 6, h: 4 } },
  { id: 'stage_dist', title: 'Portfolio by Stage', type: 'pie', defaultSize: { w: 3, h: 4 } },
  { id: 'sector_dist', title: 'Portfolio by Sector', type: 'bar', defaultSize: { w: 3, h: 4 } },
  { id: 'fund_commitment_vs_nav', title: 'Commitment vs NAV by Fund', type: 'bar', defaultSize: { w: 6, h: 4 } },
  { id: 'fund_funnel', title: 'Fund Pipeline Funnel', type: 'funnel', defaultSize: { w: 6, h: 4 } },
  { id: 'world_map', title: 'Global Investor Map', type: 'map', defaultSize: { w: 12, h: 7 } },
  { id: 'state_heatmap', title: 'State-wise Distribution', type: 'heatmap', defaultSize: { w: 4, h: 5 } },
  { id: 'city_heatmap', title: 'City-wise Distribution', type: 'heatmap', defaultSize: { w: 4, h: 5 } },
  { id: 'drawdown_by_fund', title: 'Drawdowns by Fund', type: 'bar', defaultSize: { w: 6, h: 4 } },
  { id: 'drawdown_status', title: 'Drawdown Status', type: 'pie', defaultSize: { w: 3, h: 4 } },
];

const LS_LAYOUT_KEY = 'dashboard_layout_v2';
const LS_VISIBLE_KEY = 'dashboard_visible_widgets';

const defaultLayout = CHART_WIDGETS.map((w, i) => ({
  i: w.id, x: (i % 2) * 6, y: Math.floor(i / 2) * 4, w: w.defaultSize.w, h: w.defaultSize.h, minW: 3, minH: 3,
}));

// ─── Stat Card ───
const StatCard = ({ label, value, icon: Icon, color, prefix = '', displayValue }) => (
  <div className="bg-white rounded-xl border border-gray-100 p-4 hover:shadow-md transition-shadow">
    <div className="flex items-center justify-between mb-2">
      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide" data-testid={`stat-label-${label.toLowerCase().replace(/\s+/g, '-')}`}>{label}</p>
      <div className={`p-1.5 rounded-lg ${color}`}><Icon size={14} className="text-white" /></div>
    </div>
    <p className="text-xl font-bold text-gray-900" data-testid={`stat-value-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      {displayValue != null ? displayValue : <>{prefix}{typeof value === 'number' ? fmt(value) : value}</>}
    </p>
  </div>
);

// ─── Chart Renderers ───
const RenderChart = ({ widgetId, chartData }) => {
  if (!chartData) return <div className="h-full flex items-center justify-center text-gray-400 text-sm">Loading...</div>;

  const renderCustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="bg-white border rounded-lg shadow-lg p-3 text-sm">
        <p className="font-medium text-gray-900 mb-1">{label || payload[0]?.name}</p>
        {payload.map((p, i) => (
          <p key={i} style={{ color: p.color }} className="flex justify-between gap-4">
            <span>{p.dataKey || p.name}:</span>
            <span className="font-mono font-medium">{typeof p.value === 'number' ? fmt(p.value) : p.value}</span>
          </p>
        ))}
      </div>
    );
  };

  const renderPieLabel = ({ name, percent }) => percent > 0.05 ? `${name} (${(percent * 100).toFixed(0)}%)` : '';

  switch (widgetId) {
    case 'fund_aum': {
      const d = chartData.fund_wise_aum || [];
      if (!d.length) return <p className="text-gray-400 text-sm text-center pt-8">No fund data</p>;
      return (
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={d} margin={{ top: 10, right: 20, left: 10, bottom: 40 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="fund_name" angle={-25} textAnchor="end" tick={{ fontSize: 11 }} interval={0} height={60} />
            <YAxis tickFormatter={fmt} tick={{ fontSize: 11 }} />
            <Tooltip content={renderCustomTooltip} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar dataKey="commitment" name="Commitment" fill="#3b82f6" radius={[4, 4, 0, 0]} />
            <Bar dataKey="contribution" name="Contribution" fill="#10b981" radius={[4, 4, 0, 0]} />
            <Bar dataKey="distribution" name="Distribution" fill="#f59e0b" radius={[4, 4, 0, 0]} />
            <Bar dataKey="nav" name="NAV" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      );
    }
    case 'fund_commitment_vs_nav': {
      const d = chartData.fund_wise_aum || [];
      if (!d.length) return <p className="text-gray-400 text-sm text-center pt-8">No data</p>;
      return (
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={d} margin={{ top: 10, right: 20, left: 10, bottom: 40 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="fund_name" angle={-25} textAnchor="end" tick={{ fontSize: 11 }} interval={0} height={60} />
            <YAxis tickFormatter={fmt} tick={{ fontSize: 11 }} />
            <Tooltip content={renderCustomTooltip} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar dataKey="commitment" name="Commitment" fill="#6366f1" radius={[4, 4, 0, 0]} />
            <Bar dataKey="nav" name="Current NAV" fill="#14b8a6" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      );
    }
    case 'investor_type': {
      const d = chartData.investor_type_distribution || [];
      if (!d.length) return <p className="text-gray-400 text-sm text-center pt-8">No data</p>;
      return (
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={d} dataKey="value" nameKey="name" cx="50%" cy="45%" outerRadius="70%" label={renderPieLabel} labelLine={{ strokeWidth: 1 }}>
              {d.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
            </Pie>
            <Tooltip content={renderCustomTooltip} />
          </PieChart>
        </ResponsiveContainer>
      );
    }
    case 'coverage_urgency': {
      const d = chartData.coverage_urgency_distribution || [];
      if (!d.length) return <p className="text-gray-400 text-sm text-center pt-8">No data</p>;
      return (
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={d} dataKey="value" nameKey="name" cx="50%" cy="45%" outerRadius="70%" label={renderPieLabel} labelLine={{ strokeWidth: 1 }}>
              {d.map((entry, i) => <Cell key={i} fill={URGENCY_COLORS_MAP[entry.name] || COLORS[i % COLORS.length]} />)}
            </Pie>
            <Tooltip content={renderCustomTooltip} />
          </PieChart>
        </ResponsiveContainer>
      );
    }
    case 'city_dist': {
      const d = chartData.city_distribution || [];
      if (!d.length) return <p className="text-gray-400 text-sm text-center pt-8">No city data</p>;
      return (
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={d} layout="vertical" margin={{ top: 5, right: 20, left: 80, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis type="number" tick={{ fontSize: 11 }} />
            <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={75} />
            <Tooltip content={renderCustomTooltip} />
            <Bar dataKey="value" name="Investors" fill="#8b5cf6" radius={[0, 4, 4, 0]} />
          </BarChart>
        </ResponsiveContainer>
      );
    }
    case 'state_dist': {
      const d = chartData.state_distribution || [];
      if (!d.length) return <p className="text-gray-400 text-sm text-center pt-8">No state data</p>;
      return (
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={d} layout="vertical" margin={{ top: 5, right: 20, left: 80, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis type="number" tick={{ fontSize: 11 }} />
            <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={75} />
            <Tooltip content={renderCustomTooltip} />
            <Bar dataKey="value" name="Investors" fill="#14b8a6" radius={[0, 4, 4, 0]} />
          </BarChart>
        </ResponsiveContainer>
      );
    }
    case 'contribution_timeline': {
      const d = chartData.contribution_timeline || [];
      if (!d.length) return <p className="text-gray-400 text-sm text-center pt-8">No timeline data</p>;
      return (
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={d} margin={{ top: 10, right: 20, left: 10, bottom: 20 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="month" tick={{ fontSize: 11 }} angle={-25} textAnchor="end" height={50} />
            <YAxis tickFormatter={fmt} tick={{ fontSize: 11 }} />
            <Tooltip content={renderCustomTooltip} />
            <Line type="monotone" dataKey="amount" name="Contribution" stroke="#3b82f6" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
          </LineChart>
        </ResponsiveContainer>
      );
    }
    case 'stage_dist': {
      const d = chartData.stage_distribution || [];
      if (!d.length) return <p className="text-gray-400 text-sm text-center pt-8">No stage data</p>;
      return (
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={d} dataKey="value" nameKey="name" cx="50%" cy="45%" outerRadius="70%" label={renderPieLabel} labelLine={{ strokeWidth: 1 }}>
              {d.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
            </Pie>
            <Tooltip content={renderCustomTooltip} />
          </PieChart>
        </ResponsiveContainer>
      );
    }
    case 'sector_dist': {
      const d = chartData.sector_distribution || [];
      if (!d.length) return <p className="text-gray-400 text-sm text-center pt-8">No sector data</p>;
      return (
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={d} layout="vertical" margin={{ top: 5, right: 20, left: 80, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis type="number" tickFormatter={fmt} tick={{ fontSize: 11 }} />
            <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={75} />
            <Tooltip content={renderCustomTooltip} />
            <Bar dataKey="value" name="Valuation" fill="#f59e0b" radius={[0, 4, 4, 0]} />
          </BarChart>
        </ResponsiveContainer>
      );
    }
    case 'fund_funnel': {
      const aum = chartData.fund_wise_aum || [];
      if (!aum.length) return <p className="text-gray-400 text-sm text-center pt-8">No data</p>;
      const totalCommit = aum.reduce((s, f) => s + f.commitment, 0);
      const totalContrib = aum.reduce((s, f) => s + f.contribution, 0);
      const totalDist = aum.reduce((s, f) => s + f.distribution, 0);
      const totalNav = aum.reduce((s, f) => s + f.nav, 0);
      const funnelData = [
        { name: 'Commitment', value: totalCommit, fill: '#3b82f6' },
        { name: 'Contribution', value: totalContrib, fill: '#10b981' },
        { name: 'Current NAV', value: totalNav, fill: '#8b5cf6' },
        { name: 'Distribution', value: totalDist, fill: '#f59e0b' },
      ].filter(d => d.value > 0);
      if (!funnelData.length) return <p className="text-gray-400 text-sm text-center pt-8">No data</p>;
      return (
        <ResponsiveContainer width="100%" height="100%">
          <FunnelChart>
            <Tooltip formatter={(v) => fmt(v)} />
            <Funnel dataKey="value" data={funnelData} isAnimationActive>
              <LabelList position="center" fill="#fff" stroke="none" fontSize={12} formatter={(v) => fmt(v)} />
              <LabelList position="right" fill="#374151" stroke="none" fontSize={12} dataKey="name" />
            </Funnel>
          </FunnelChart>
        </ResponsiveContainer>
      );
    }
    case 'world_map': {
      const geoData = chartData.geo_data || [];
      const countryData = chartData.country_distribution || [];
      return <WorldMapWidget geoData={geoData} countryData={countryData} />;
    }
    case 'state_heatmap': {
      const stateData = chartData.state_distribution || [];
      return <StateHeatmapWidget stateData={stateData} />;
    }
    case 'city_heatmap': {
      const cityData = chartData.city_distribution || [];
      return <CityHeatmapWidget cityData={cityData} />;
    }
    case 'drawdown_by_fund': {
      const d = chartData.drawdown_by_fund || [];
      if (!d.length) return <p className="text-gray-400 text-sm text-center pt-8">No drawdown data</p>;
      return (
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={d} margin={{ top: 10, right: 20, left: 10, bottom: 40 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="fund_name" angle={-15} textAnchor="end" tick={{ fontSize: 11 }} interval={0} height={50} />
            <YAxis tickFormatter={fmt} tick={{ fontSize: 11 }} />
            <Tooltip content={renderCustomTooltip} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar dataKey="called" name="Called" fill="#3b82f6" radius={[4, 4, 0, 0]} />
            <Bar dataKey="received" name="Received" fill="#10b981" radius={[4, 4, 0, 0]} />
            <Bar dataKey="outstanding" name="Outstanding" fill="#ef4444" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      );
    }
    case 'drawdown_status': {
      const d = chartData.drawdown_status_distribution || [];
      if (!d.length) return <p className="text-gray-400 text-sm text-center pt-8">No drawdown data</p>;
      return (
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={d} cx="50%" cy="50%" outerRadius="70%" dataKey="value" nameKey="name" label={renderPieLabel} labelLine={false}>
              {d.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
            </Pie>
            <Tooltip content={renderCustomTooltip} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
          </PieChart>
        </ResponsiveContainer>
      );
    }
    default:
      return <p className="text-gray-400 text-sm text-center pt-8">Unknown chart</p>;
  }
};

const Dashboard = () => {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [chartData, setChartData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [containerWidth, setContainerWidth] = useState(1200);
  const containerRef = React.useRef(null);

  // Currency + display-unit toggle (persisted in localStorage)
  const { formatFromBuckets, controlsUI } = useCurrencyToggle('dashboard_currency');

  // Layout state
  const [layouts, setLayouts] = useState(() => {
    try { const saved = localStorage.getItem(LS_LAYOUT_KEY); return saved ? JSON.parse(saved) : { lg: defaultLayout }; }
    catch { return { lg: defaultLayout }; }
  });
  const [visibleWidgets, setVisibleWidgets] = useState(() => {
    try { const saved = localStorage.getItem(LS_VISIBLE_KEY); return saved ? JSON.parse(saved) : CHART_WIDGETS.map(w => w.id); }
    catch { return CHART_WIDGETS.map(w => w.id); }
  });
  const [showWidgetPicker, setShowWidgetPicker] = useState(false);

  useEffect(() => {
    Promise.all([dashboardAPI.getSummary(), dashboardAPI.getChartData()])
      .then(([sumRes, chartRes]) => { setSummary(sumRes.data); setChartData(chartRes.data); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const measure = () => {
      if (containerRef.current) setContainerWidth(containerRef.current.offsetWidth);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  const onLayoutChange = (layout, allLayouts) => {
    setLayouts(allLayouts);
    localStorage.setItem(LS_LAYOUT_KEY, JSON.stringify(allLayouts));
  };

  const toggleWidget = (id) => {
    setVisibleWidgets(prev => {
      const next = prev.includes(id) ? prev.filter(w => w !== id) : [...prev, id];
      localStorage.setItem(LS_VISIBLE_KEY, JSON.stringify(next));
      return next;
    });
  };

  const resetLayout = () => {
    const reset = { lg: defaultLayout };
    const allIds = CHART_WIDGETS.map(w => w.id);
    setLayouts(reset);
    setVisibleWidgets(allIds);
    localStorage.setItem(LS_LAYOUT_KEY, JSON.stringify(reset));
    localStorage.setItem(LS_VISIBLE_KEY, JSON.stringify(allIds));
  };

  const ep = user?.effective_permissions || user?.module_permissions || {};
  const showInvestors = ep.investors === true;

  const visibleCharts = CHART_WIDGETS.filter(w => visibleWidgets.includes(w.id));
  const currentLayout = (layouts.lg || defaultLayout).filter(l => visibleWidgets.includes(l.i));

  // Add missing layout items for newly visible widgets
  const completeLayout = visibleCharts.map(w => {
    const existing = currentLayout.find(l => l.i === w.id);
    if (existing) return existing;
    const maxY = currentLayout.reduce((m, l) => Math.max(m, l.y + l.h), 0);
    return { i: w.id, x: 0, y: maxY, w: w.defaultSize.w, h: w.defaultSize.h, minW: 3, minH: 3 };
  });

  return (
    <div data-testid="dashboard-page" ref={containerRef}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900" data-testid="dashboard-title">
            Welcome back, {user?.name}!
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">Fund management overview and analytics</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {controlsUI}
          {editMode && (
            <>
              <button onClick={() => setShowWidgetPicker(p => !p)}
                className="flex items-center gap-1.5 px-3 py-2 bg-blue-100 text-blue-700 rounded-lg text-sm font-medium hover:bg-blue-200"
                data-testid="widget-picker-btn">
                <Plus size={14} /> Widgets
              </button>
              <button onClick={resetLayout} className="px-3 py-2 border rounded-lg text-sm text-gray-600 hover:bg-gray-50">Reset Layout</button>
            </>
          )}
          <button onClick={() => { setEditMode(p => !p); setShowWidgetPicker(false); }}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium ${editMode ? 'bg-green-600 text-white hover:bg-green-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
            data-testid="customize-dashboard-btn">
            {editMode ? (<><Save size={14} /> Done</>) : (<><Settings size={14} /> Customize</>)}
          </button>
        </div>
      </div>

      {/* Widget picker dropdown */}
      {showWidgetPicker && editMode && (
        <div className="bg-white border rounded-xl shadow-lg p-4 mb-4" data-testid="widget-picker-panel">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Toggle Chart Widgets</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
            {CHART_WIDGETS.map(w => {
              const visible = visibleWidgets.includes(w.id);
              return (
                <button key={w.id} onClick={() => toggleWidget(w.id)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm text-left transition-colors ${visible ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 text-gray-500 hover:border-gray-300'}`}
                  data-testid={`toggle-widget-${w.id}`}>
                  {visible ? <Eye size={14} /> : <EyeOff size={14} />}
                  <span className="truncate">{w.title}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Summary Stats */}
      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          {[1, 2, 3, 4].map(i => <div key={i} className="bg-white rounded-xl border p-4 animate-pulse"><div className="h-4 bg-gray-200 rounded w-20 mb-2" /><div className="h-6 bg-gray-200 rounded w-14" /></div>)}
        </div>
      ) : summary && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4" data-testid="dashboard-counts">
            <StatCard label="Funds" value={summary.funds_count} icon={FolderKanban} color="bg-blue-500" />
            <StatCard label="Portfolio" value={summary.investments_count} icon={TrendingUp} color="bg-emerald-500" />
            <StatCard label="Contacts" value={summary.contacts_count} icon={UserCheck} color="bg-violet-500" />
            <StatCard label="Accounts" value={summary.accounts_count} icon={Briefcase} color="bg-amber-500" />
          </div>
          {showInvestors && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4" data-testid="dashboard-financials">
              <StatCard label="Commitment" displayValue={formatFromBuckets(summary.totals_by_currency, 'commitment')} icon={DollarSign} color="bg-indigo-500" />
              <StatCard label="Contribution" displayValue={formatFromBuckets(summary.totals_by_currency, 'contribution')} icon={BarChart3} color="bg-teal-500" />
              <StatCard label="Distribution" displayValue={formatFromBuckets(summary.totals_by_currency, 'distribution')} icon={ArrowUpRight} color="bg-rose-500" />
              <StatCard label="Total NAV" displayValue={formatFromBuckets(summary.totals_by_currency, 'nav')} icon={PieIcon} color="bg-cyan-500" />
            </div>
          )}
          {summary.drawdowns_count > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6" data-testid="dashboard-drawdowns">
              <StatCard label="Drawdowns" value={summary.drawdowns_count} icon={FolderKanban} color="bg-orange-500" />
              <StatCard label="Capital Called" displayValue={formatFromBuckets(summary.totals_by_currency, 'called')} icon={DollarSign} color="bg-blue-600" />
              <StatCard label="Capital Received" displayValue={formatFromBuckets(summary.totals_by_currency, 'received')} icon={TrendingUp} color="bg-green-600" />
            </div>
          )}
        </>
      )}

      {/* Charts Grid */}
      {visibleCharts.length > 0 && (
        <ResponsiveGridLayout
          className="layout"
          layouts={{ lg: completeLayout }}
          breakpoints={{ lg: 1200, md: 996, sm: 768 }}
          cols={{ lg: 12, md: 6, sm: 1 }}
          rowHeight={80}
          width={containerWidth}
          isDraggable={editMode}
          isResizable={editMode}
          onLayoutChange={onLayoutChange}
          draggableHandle=".drag-handle"
          margin={[16, 16]}
        >
          {visibleCharts.map(w => (
            <div key={w.id} className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden" data-testid={`chart-widget-${w.id}`}>
              <div className={`flex items-center justify-between px-4 py-2.5 border-b bg-gray-50/60 ${editMode ? 'drag-handle cursor-grab' : ''}`}>
                <div className="flex items-center gap-2">
                  {editMode && <GripVertical size={14} className="text-gray-400" />}
                  <h3 className="text-sm font-semibold text-gray-700">{w.title}</h3>
                </div>
                {editMode && (
                  <button onClick={() => toggleWidget(w.id)} className="p-1 text-gray-400 hover:text-red-500"><X size={14} /></button>
                )}
              </div>
              <div className="p-3" style={{ height: 'calc(100% - 44px)' }}>
                <RenderChart widgetId={w.id} chartData={chartData} />
              </div>
            </div>
          ))}
        </ResponsiveGridLayout>
      )}

      {visibleCharts.length === 0 && !loading && (
        <div className="text-center py-16 bg-white rounded-xl border">
          <LayoutDashboard size={48} className="mx-auto text-gray-300 mb-4" />
          <p className="text-gray-500 mb-2">No charts visible</p>
          <p className="text-sm text-gray-400">Click "Customize" and add widgets to build your dashboard</p>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
